import React, { useState } from 'react';
import {
  Box, Button, TextField, Typography, Paper, CircularProgress, Link,
  List, ListItemButton, ListItemText, Chip, MenuItem, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  login, treeSearch, personSpouses, startClaim, startNewMember, verifyOtp, resendOtp,
  forgotPassword, resetPassword,
} from '../api';

/**
 * LoginPage + tree-linked registration wizard.
 *
 * Sign in: phone (or email) + password.
 * Register:
 *   invite  -> enter family invite code
 *   type    -> "I am in the tree" | "I am new to the tree"
 *   CLAIM path:  find-self -> phone -> otp (code + password) -> done
 *   NEW path:    find-mother -> pick-father -> details+phone -> otp -> done
 */
const LoginPage = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [step, setStep] = useState('invite');
  const [hasEmailOnFile, setHasEmailOnFile] = useState(false);
  const [loginChoices, setLoginChoices] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // Login fields
  const [identifier, setIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot / reset password fields
  const [forgotStep, setForgotStep] = useState('request'); // 'request' | 'reset'
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPw, setResetNewPw] = useState('');
  const [resetConfirmPw, setResetConfirmPw] = useState('');
  const [resetAccounts, setResetAccounts] = useState([]);

  // Wizard state
  const [inviteCode, setInviteCode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [selectedSelf, setSelectedSelf] = useState(null);
  const [mother, setMother] = useState(null);
  const [fatherOptions, setFatherOptions] = useState([]);
  const [fatherId, setFatherId] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const fail = (err, fallback) => {
    setError(err.response?.data?.error || fallback || 'Something went wrong. Please try again.');
    // Make sure the error is visible even on small screens
    setTimeout(() => {
      document.getElementById('mm-error-text')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const run = async (fn) => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------- actions ------------------------- */

  const handleLogin = (accountId) =>
    run(async () => {
      const result = await login(identifier, loginPassword, accountId);
      if (result.chooseAccount) {
        setLoginChoices(result.accounts);
        return;
      }
      onLogin(result.user);
    });

  const openForgot = () => {
    setMode('forgot');
    setForgotStep('request');
    setError(''); setInfo('');
    setResetOtp(''); setResetNewPw(''); setResetConfirmPw(''); setResetAccounts([]);
  };

  const handleForgotRequest = () =>
    run(async () => {
      const data = await forgotPassword(identifier);
      setInfo(data.message);
      setForgotStep('reset');
    });

  const handleResetSubmit = (accountId) =>
    run(async () => {
      if (resetNewPw !== resetConfirmPw) {
        setError('The two passwords do not match.');
        return;
      }
      const result = await resetPassword(identifier, resetOtp, resetNewPw, accountId);
      if (result.chooseAccount) {
        setResetAccounts(result.accounts);
        return;
      }
      onLogin(result.user); // reset succeeded → signed straight in
    });

  const handleSearchSelf = () =>
    run(async () => {
      const r = await treeSearch(searchTerm, inviteCode);
      setResults(r);
      if (r.length === 0) setInfo('No one found with that name. Try a different name, or go back and choose "I am new to the tree".');
    });

  const handleSearchMother = () =>
    run(async () => {
      const r = await treeSearch(searchTerm, inviteCode, 'FEMALE');
      setResults(r);
      if (r.length === 0) setInfo('No mother found with that name. Try one of her other names.');
    });

  const choosePersonAsSelf = (p) => {
    if (p.claimed) {
      setError(`${p.name} already has an account. If this is you and you did not register, contact the family admin.`);
      return;
    }
    setSelectedSelf(p);
    setError('');
    setStep('claim-phone');
  };

  const chooseMother = (m) =>
    run(async () => {
      setMother(m);
      const spouses = await personSpouses(m.id, inviteCode);
      setFatherOptions(spouses.filter((s) => s.gender === 'MALE'));
      setFatherId('');
      setStep('pick-father');
    });

  const handleStartClaim = () =>
    run(async () => {
      const data = await startClaim({ personId: selectedSelf.id, phone, email, inviteCode });
      setPendingId(data.pendingId);
      setInfo(data.message);
      setHasEmailOnFile(data.hasEmail || Boolean(email && email.trim()));
      setStep('otp');
    });

  const handleStartNewMember = () =>
    run(async () => {
      const data = await startNewMember({
        name: fullName, gender, birthDate, motherId: mother.id,
        fatherId: fatherId || undefined, phone, email, inviteCode,
      });
      setPendingId(data.pendingId);
      setInfo(data.message);
      setHasEmailOnFile(data.hasEmail || Boolean(email && email.trim()));
      setStep('otp');
    });

  const handleVerify = () =>
    run(async () => {
      if (newPassword !== confirmPassword) {
        setError('The two passwords do not match.');
        return;
      }
      const user = await verifyOtp({ pendingId, otp, password: newPassword });
      onLogin(user);
    });

  const handleResendVia = (channel) =>
    run(async () => {
      const data = await resendOtp(pendingId, channel);
      setInfo(data.message);
    });

  const resetWizard = () => {
    setStep('invite');
    setError(''); setInfo('');
    setSearchTerm(''); setResults([]);
    setSelectedSelf(null); setMother(null); setFatherOptions([]); setFatherId('');
    setFullName(''); setGender(''); setBirthDate('');
    setPhone(''); setEmail(''); setPendingId(null);
    setOtp(''); setNewPassword(''); setConfirmPassword('');
  };

  const goBack = () => {
    setError(''); setInfo('');
    const backMap = {
      type: 'invite',
      'find-self': 'type',
      'claim-phone': 'find-self',
      'find-mother': 'type',
      'pick-father': 'find-mother',
      details: 'pick-father',
      otp: selectedSelf ? 'claim-phone' : 'details',
    };
    setStep(backMap[step] || 'invite');
  };

  const onKey = (action) => (e) => {
    if (e.key === 'Enter' && !loading) action();
  };

  /* ------------------------- rendering ------------------------- */

  const PersonResultList = ({ onPick, pickLabel }) => (
    <List sx={{ maxHeight: 300, overflowY: 'auto', mt: 1 }}>
      {results.map((p) => (
        <ListItemButton
          key={p.id}
          onClick={() => onPick(p)}
          sx={{ border: '1px solid #eee', borderRadius: 2, mb: 1 }}
        >
          <ListItemText
            primary={p.name}
            secondary={[
              p.motherName ? `Mother: ${p.motherName}` : null,
              p.fatherName ? `Father: ${p.fatherName}` : null,
            ].filter(Boolean).join(' • ') || 'Parents not recorded'}
          />
          {p.claimed ? (
            <Chip label="Has account" size="small" color="default" />
          ) : (
            <Chip label={pickLabel} size="small" color="primary" variant="outlined" />
          )}
        </ListItemButton>
      ))}
    </List>
  );

  const renderRegister = () => {
    switch (step) {
      case 'invite':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter the family invite code to begin. Ask a family admin if you don't have it.
            </Typography>
            <TextField label="Family invite code" fullWidth margin="normal"
              value={inviteCode} onChange={(e) => setInviteCode(e.target.value)}
              onKeyDown={onKey(() => inviteCode.trim() && setStep('type'))} />
            <Button fullWidth variant="contained" size="large" sx={btnSx} disabled={!inviteCode.trim()}
              onClick={() => setStep('type')}>
              Continue
            </Button>
          </>
        );

      case 'type':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you already recorded in the family tree?
            </Typography>
            <Stack spacing={2}>
              <Button variant="contained" size="large" sx={btnSx}
                onClick={() => { setSearchTerm(''); setResults([]); setStep('find-self'); }}>
                I am in the tree — find me
              </Button>
              <Button variant="outlined" size="large"
                onClick={() => { setSearchTerm(''); setResults([]); setStep('find-mother'); }}>
                I am new — add me under my mother
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Spouses who married into the family: ask your husband or wife to add you from
              their account first, then register here by finding yourself in the tree.
            </Typography>
          </>
        );

      case 'find-self':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Search your name as it appears in the family tree, then select yourself.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Your name" fullWidth value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={onKey(handleSearchSelf)} />
              <Button variant="contained" onClick={handleSearchSelf} disabled={loading || searchTerm.trim().length < 3}>
                Search
              </Button>
            </Box>
            <PersonResultList onPick={choosePersonAsSelf} pickLabel="This is me" />
          </>
        );

      case 'claim-phone':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Registering as <b>{selectedSelf?.name}</b>. Enter your email — we'll send your
              verification code there. Also add your phone number (used to sign in, and as a
              backup if email doesn't work for you).
            </Typography>
            <TextField label="Email" type="email" fullWidth margin="normal"
              value={email} onChange={(e) => setEmail(e.target.value)}
              helperText="Your code will be sent here" />
            <TextField label="Phone number" type="tel" fullWidth margin="normal"
              placeholder="e.g. 0712345678" value={phone}
              onChange={(e) => setPhone(e.target.value)} onKeyDown={onKey(handleStartClaim)}
              helperText="This is the number you will use to sign in" />
            <Button fullWidth variant="contained" size="large" sx={btnSx}
              onClick={handleStartClaim} disabled={loading || !phone.trim()}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send me the code'}
            </Button>
          </>
        );

      case 'find-mother':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You join the tree through your <b>mother</b>. Enter one of her names and select
              her from the list.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Your mother's name" fullWidth value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={onKey(handleSearchMother)} />
              <Button variant="contained" onClick={handleSearchMother} disabled={loading || searchTerm.trim().length < 3}>
                Search
              </Button>
            </Box>
            <PersonResultList onPick={chooseMother} pickLabel="My mother" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              If only your father is in the tree, the Family President must add you — please contact them.
            </Typography>
          </>
        );

      case 'pick-father':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Mother: <b>{mother?.name}</b>. Now select your father, if he is listed.
            </Typography>
            <TextField select fullWidth margin="normal" label="Your father"
              value={fatherId} onChange={(e) => setFatherId(e.target.value)}>
              {fatherOptions.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
              <MenuItem value="">Not listed / not applicable</MenuItem>
            </TextField>
            <Button fullWidth variant="contained" size="large" sx={btnSx} onClick={() => setStep('details')}>
              Continue
            </Button>
          </>
        );

      case 'details':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your details — as they should appear in the family tree.
            </Typography>
            <TextField label="Your full name" fullWidth margin="normal"
              value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <TextField select fullWidth margin="normal" label="Gender"
              value={gender} onChange={(e) => setGender(e.target.value)}>
              <MenuItem value="MALE">Male</MenuItem>
              <MenuItem value="FEMALE">Female</MenuItem>
            </TextField>
            <TextField label="Date of birth (optional)" type="date" fullWidth margin="normal"
              InputLabelProps={{ shrink: true }} value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)} />
            <TextField label="Email" type="email" fullWidth margin="normal"
              value={email} onChange={(e) => setEmail(e.target.value)}
              helperText="Your verification code will be sent here" />
            <TextField label="Phone number" type="tel" fullWidth margin="normal"
              placeholder="e.g. 0712345678" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              helperText="Used to sign in, and as a backup if email doesn't work for you" />
            <Button fullWidth variant="contained" size="large" sx={btnSx}
              onClick={handleStartNewMember}
              disabled={loading || !fullName.trim() || !gender || !phone.trim()}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send me the code'}
            </Button>
          </>
        );

      case 'otp':
        return (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {hasEmailOnFile
                ? <>Enter the 6-digit code sent to your <b>email</b>, then choose your password.</>
                : <>Enter the 6-digit code sent to <b>{phone}</b> by SMS, then choose your password.</>}
            </Typography>
            <TextField label="Verification code" fullWidth margin="normal" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }} />
            <TextField label="Choose a password" type="password" fullWidth margin="normal"
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              helperText="At least 8 characters — you will sign in with your phone number and this password" />
            <TextField label="Repeat the password" type="password" fullWidth margin="normal"
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={onKey(handleVerify)} />
            <Button fullWidth variant="contained" size="large" sx={btnSx}
              onClick={handleVerify}
              disabled={loading || otp.length !== 6 || newPassword.length < 8}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify and create my account'}
            </Button>
            {hasEmailOnFile ? (
              <>
                <Button fullWidth sx={{ mt: 1 }} onClick={() => handleResendVia('email')} disabled={loading}>
                  Resend code by email
                </Button>
                <Button fullWidth sx={{ mt: 0.5 }} onClick={() => handleResendVia('sms')} disabled={loading}>
                  Can't find the email? Send the code by SMS instead
                </Button>
              </>
            ) : (
              <Button fullWidth sx={{ mt: 1 }} onClick={() => handleResendVia('sms')} disabled={loading}>
                Resend code by SMS
              </Button>
            )}
          </>
        );

      default:
        return null;
    }
  };

  const renderForgot = () => {
    if (resetAccounts.length > 0) {
      return (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Whose password?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            More than one family member uses this phone. Tap the account to reset.
          </Typography>
          {resetAccounts.map((acc) => (
            <Button key={acc.id} fullWidth variant="outlined" sx={{ mb: 1, justifyContent: 'flex-start' }}
              onClick={() => handleResetSubmit(acc.id)} disabled={loading}>
              {acc.name}
            </Button>
          ))}
        </>
      );
    }
    if (forgotStep === 'request') {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter your phone number (or email). We'll send you a code to reset your password.
          </Typography>
          <TextField label="Phone number (or email)" fullWidth margin="normal"
            placeholder="e.g. 0712345678" value={identifier}
            onChange={(e) => setIdentifier(e.target.value)} onKeyDown={onKey(handleForgotRequest)} />
          <Button fullWidth variant="contained" size="large" sx={btnSx}
            onClick={handleForgotRequest} disabled={loading || !identifier.trim()}>
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send reset code'}
          </Button>
        </>
      );
    }
    return (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Enter the 6-digit code we sent, then choose a new password.
        </Typography>
        <TextField label="Reset code" fullWidth margin="normal" value={resetOtp}
          onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputProps={{ inputMode: 'numeric' }} />
        <TextField label="New password" type="password" fullWidth margin="normal"
          value={resetNewPw} onChange={(e) => setResetNewPw(e.target.value)}
          helperText="At least 8 characters" />
        <TextField label="Repeat new password" type="password" fullWidth margin="normal"
          value={resetConfirmPw} onChange={(e) => setResetConfirmPw(e.target.value)}
          onKeyDown={onKey(() => handleResetSubmit())} />
        <Button fullWidth variant="contained" size="large" sx={btnSx}
          onClick={() => handleResetSubmit()}
          disabled={loading || resetOtp.length !== 6 || resetNewPw.length < 8}>
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Reset password & sign in'}
        </Button>
        <Button fullWidth sx={{ mt: 1 }} onClick={handleForgotRequest} disabled={loading}>
          Didn't get it? Resend code
        </Button>
      </>
    );
  };

  const btnSx = {
    mt: 2, py: 1.2,
    color: '#fff',
    background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)',
    '&:hover': { background: '#047857' },
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)', padding: 2,
    }}>
      <Paper elevation={8} sx={{
        p: 4, width: '100%', maxWidth: 440, borderRadius: 3,
        maxHeight: '92vh', overflowY: 'auto',
      }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 1 }}>
          Mbaa Mwendwa
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          {mode === 'login'
            ? 'Welcome back. Sign in to view the family.'
            : mode === 'forgot'
            ? 'Reset your password with a code sent to your phone or email.'
            : 'Create your account — linked to your place in the family tree.'}
        </Typography>

        {mode === 'register' && step !== 'invite' && (
          <Button startIcon={<ArrowBackIcon />} size="small" onClick={goBack} sx={{ mb: 1 }}>
            Back
          </Button>
        )}

        {mode === 'login' ? (
          loginChoices.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Which one are you?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                More than one family member uses this phone with this password. Tap your name.
              </Typography>
              {loginChoices.map((acc) => (
                <Button key={acc.id} fullWidth variant="outlined" sx={{ mb: 1, justifyContent: 'flex-start' }}
                  onClick={() => handleLogin(acc.id)} disabled={loading}>
                  {acc.name}
                </Button>
              ))}
              {error && <Typography id="mm-error-text" color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
              <Button fullWidth sx={{ mt: 1 }} onClick={() => setLoginChoices([])}>Back</Button>
            </>
          ) : (
          <>
            <TextField label="Phone number (or email)" fullWidth margin="normal"
              placeholder="e.g. 0712345678" value={identifier}
              onChange={(e) => setIdentifier(e.target.value)} onKeyDown={onKey(() => handleLogin())} />
            <TextField label="Password" type="password" fullWidth margin="normal"
              value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={onKey(() => handleLogin())} />
            {error && <Typography id="mm-error-text" color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
            <Button fullWidth variant="contained" size="large" sx={btnSx}
              onClick={() => handleLogin()} disabled={loading}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign in'}
            </Button>
            <Typography variant="body2" align="center" sx={{ mt: 1.5 }}>
              <Link component="button" onClick={openForgot}>Forgot password?</Link>
            </Typography>
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              New family member?{' '}
              <Link component="button" onClick={() => { setMode('register'); resetWizard(); }}>
                Register here
              </Link>
            </Typography>
          </>
          )
        ) : mode === 'forgot' ? (
          <>
            {renderForgot()}
            {error && <Typography id="mm-error-text" color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
            {info && !error && <Typography color="primary" variant="body2" sx={{ mt: 1 }}>{info}</Typography>}
            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              <Link component="button" onClick={() => { setMode('login'); setError(''); setInfo(''); setResetAccounts([]); }}>
                Back to sign in
              </Link>
            </Typography>
          </>
        ) : (
          <>
            {renderRegister()}
            {error && <Typography id="mm-error-text" color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
            {info && !error && <Typography color="primary" variant="body2" sx={{ mt: 1 }}>{info}</Typography>}
            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              Already have an account?{' '}
              <Link component="button" onClick={() => { setMode('login'); setError(''); setInfo(''); }}>
                Sign in
              </Link>
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LoginPage;
